import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { MainLayout } from "../components/layout";
import api from "../services/api";
import { useNavigate } from "../lib/router";

interface PendingUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  employeeId: string;
  departmentId: string | null;
  position: string | null;
  status: string;
}

export function UserApproval() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [processingUserId, setProcessingUserId] = useState<string | null>(null);

  // Check if user is admin or HR
  const isAdminOrHR = user?.role === "admin" || user?.role === "hr";

  // Fetch pending users
  const {
    data: pendingUsersData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["pending-users"],
    queryFn: async () => {
      const response = await api.get<{ data: { users: PendingUser[] } }>(
        "/api/v1/auth/pending-users"
      );
      return response.data.data.users;
    },
    enabled: !!user && isAdminOrHR,
  });

  // Approve user mutation
  const approveMutation = useMutation({
    mutationFn: async (userId: string) => {
      await api.post(`/api/v1/auth/approve-user/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-users"] });
      setProcessingUserId(null);
    },
    onError: (error: any) => {
      console.error("Approve error:", error);
      alert(error.response?.data?.error?.message || "Failed to approve user");
      setProcessingUserId(null);
    },
  });

  // Reject user mutation
  const rejectMutation = useMutation({
    mutationFn: async (userId: string) => {
      await api.post(`/api/v1/auth/reject-user/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-users"] });
      setProcessingUserId(null);
    },
    onError: (error: any) => {
      console.error("Reject error:", error);
      alert(error.response?.data?.error?.message || "Failed to reject user");
      setProcessingUserId(null);
    },
  });

  const handleApprove = (userId: string) => {
    if (
      confirm(
        "คุณแน่ใจหรือไม่ที่จะอนุมัติผู้ใช้นี้? ผู้ใช้จะสามารถเข้าสู่ระบบได้ทันที"
      )
    ) {
      setProcessingUserId(userId);
      approveMutation.mutate(userId);
    }
  };

  const handleReject = (userId: string) => {
    if (
      confirm("คุณแน่ใจหรือไม่ที่จะปฏิเสธผู้ใช้นี้? ข้อมูลจะถูกลบออกจากระบบ")
    ) {
      setProcessingUserId(userId);
      rejectMutation.mutate(userId);
    }
  };

  if (!user) {
    window.location.href = "/login";
    return null;
  }

  if (!isAdminOrHR) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>ไม่มีสิทธิ์เข้าถึง</CardTitle>
            <CardDescription>
              เฉพาะผู้ดูแลระบบและ HR เท่านั้นที่สามารถอนุมัติผู้ใช้ได้
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/")}>กลับหน้าหลัก</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <MainLayout title="✅ อนุมัติผู้ใช้งานใหม่">
      <main className="max-w-4xl mx-auto">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">
              กำลังโหลดข้อมูล...
            </p>
          </div>
        ) : error ? (
          <Card>
            <CardContent className="py-8">
              <div className="text-center text-red-600">
                <p className="text-lg font-semibold mb-2">
                  เกิดข้อผิดพลาดในการโหลดข้อมูล
                </p>
                <p className="text-sm">
                  {(error as any).response?.data?.error?.message ||
                    "กรุณาลองใหม่อีกครั้ง"}
                </p>
                <Button
                  className="mt-4"
                  onClick={() =>
                    queryClient.invalidateQueries({
                      queryKey: ["pending-users"],
                    })
                  }
                >
                  โหลดใหม่
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : !pendingUsersData || pendingUsersData.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <div className="mx-auto w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
                  <svg
                    className="w-12 h-12 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  ไม่มีผู้ใช้ที่รออนุมัติ
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  ขณะนี้ไม่มีคำขอสมัครสมาชิกที่ต้องการการอนุมัติ
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {pendingUsersData.map((pendingUser) => (
              <Card key={pendingUser.id}>
                <CardContent className="py-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                          <span className="text-lg font-bold text-blue-600 dark:text-blue-300">
                            {pendingUser.firstName.charAt(0)}
                            {pendingUser.lastName.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            {pendingUser.firstName} {pendingUser.lastName}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {pendingUser.email}
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">
                            รหัสพนักงาน:
                          </span>
                          <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                            {pendingUser.employeeId}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">
                            ตำแหน่ง:
                          </span>
                          <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                            {pendingUser.position || "-"}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-3 ml-6">
                      <Button
                        onClick={() => handleApprove(pendingUser.id)}
                        disabled={
                          processingUserId === pendingUser.id ||
                          approveMutation.isPending ||
                          rejectMutation.isPending
                        }
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {processingUserId === pendingUser.id &&
                        approveMutation.isPending ? (
                          <>
                            <span className="animate-spin mr-2">⏳</span>
                            กำลังอนุมัติ...
                          </>
                        ) : (
                          <>✓ อนุมัติ</>
                        )}
                      </Button>
                      <Button
                        onClick={() => handleReject(pendingUser.id)}
                        disabled={
                          processingUserId === pendingUser.id ||
                          approveMutation.isPending ||
                          rejectMutation.isPending
                        }
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        {processingUserId === pendingUser.id &&
                        rejectMutation.isPending ? (
                          <>
                            <span className="animate-spin mr-2">⏳</span>
                            กำลังปฏิเสธ...
                          </>
                        ) : (
                          <>✕ ปฏิเสธ</>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </MainLayout>
  );
}

export default UserApproval;
